// src/services/paymentService.js
/**
 * Payment service that integrates with Stripe via API routes
 */

// Stripe plans configuration
export const PAYMENT_PLANS = {
  MONTHLY: {
    id: 'pro_monthly',
    name: 'Monthly Pro',
    price: 1.99,
    currency: 'USD',
    interval: 'monthly',
    description: 'Unlimited access to Pro features, billed monthly'
  },
  ANNUAL: {
    id: 'pro_annual',
    name: 'Annual Pro',
    price: 19.99,
    currency: 'USD',
    interval: 'annual',
    description: 'Unlimited access to Pro features, billed annually (save 16%)'
  }
};

/**
 * Cancel a subscription
 * @param {string} subscriptionId - The subscription ID to cancel
 * @returns {Promise<{success: boolean}>} Cancellation result
 */
export const cancelSubscription = async (subscriptionId) => {
  try {
    console.log('Calling cancelSubscription with:', { subscriptionId });
    
    const response = await fetch(`/api/stripe/cancel-subscription`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ subscriptionId }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error cancelling subscription:', error);
    throw new Error(error.message || 'Failed to cancel subscription');
  }
};

/**
 * Create a Stripe checkout session for subscription
 * @param {Object} plan - Plan details from PAYMENT_PLANS
 * @param {string} uid - User ID
 * @param {string} email - User email
 * @returns {Promise<{url: string}>} - Stripe checkout URL
 */
export const createCheckoutSession = async (plan, uid, email) => {
  try {
    console.log('Creating checkout with plan:', plan, 'for user:', uid, 'email:', email);
    
    // Prepare the plan data - ensure we have a valid object
    const planData = typeof plan === 'string' 
      ? { id: plan } 
      : plan;
    
    const response = await fetch(`/api/stripe/create-checkout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        plan: planData, 
        userId: uid,
        email 
      }),
    });
    
    // Clone the response so we can read it multiple times if needed
    const clonedResponse = response.clone();
    
    try {
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || `HTTP error ${response.status}`);
      }
      return { url: data.url };
    } catch (e) {
      // If JSON parsing fails, try to get the raw text
      const text = await clonedResponse.text();
      throw new Error(`Failed to process response: ${text.slice(0, 100)}...`);
    }
    // This code is no longer needed as we handle the return in the try block above
  } catch (error) {
    console.error('Error creating checkout session:', error);
    throw new Error(error.message || 'Failed to initialize checkout session');
  }
};

/**
 * Verify the subscription status after a Stripe checkout
 * @param {string} sessionId - The Stripe checkout session ID
 * @param {string} userId - The user ID
 * @returns {Promise<{success: boolean, subscription: Object}>} - Result object
 */
export const verifyCheckoutSession = async (sessionId, userId) => {
  try {
    console.log('Verifying checkout session:', { sessionId, userId });
    
    const response = await fetch(`/api/stripe/verify-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        sessionId,
        userId 
      }),
    });
    
    // Clone the response immediately
    const clonedResponse = response.clone();
    
    try {
      const data = await response.json();
      console.log('Verify session response:', data);
      
      if (!response.ok) {
        throw new Error(data.error || `HTTP error ${response.status}`);
      }
      
      // Validate the response data
      if (!data || typeof data.success !== 'boolean') {
        throw new Error('Invalid response format from verify-session');
      }
      
      if (!data.success) {
        throw new Error(data.error || 'Verification failed');
      }
      
      if (!data.subscription) {
        throw new Error('No subscription data in response');
      }
      
      return data;
    } catch (e) {
      // If JSON parsing fails or validation fails, try to get the text content
      const text = await clonedResponse.text();
      console.error('Response error:', {
        error: e,
        message: e.message,
        responseText: text.slice(0, 500),
        status: response.status,
        headers: Object.fromEntries(response.headers.entries())
      });
      throw new Error(e.message || 'Failed to process verification response');
    }
  } catch (error) {
    console.error('Error verifying checkout session:', {
      error,
      message: error.message,
      sessionId,
      userId
    });
    
    // Always return a consistent error format
    return { 
      success: false, 
      error: error.message || 'Failed to verify subscription',
      details: error.stack
    };
  }
};

/**
 * Get user subscription status from Stripe
 * @param {string} uid - User ID
 * @param {string} email - User email
 * @returns {Promise<{tier: string, subscription: Object|null}>} - User subscription data
 */
export const getSubscriptionStatus = async (uid, email) => {
  try {
    const response = await fetch(
      `/api/stripe/customer?userId=${uid}&email=${encodeURIComponent(email)}`
    );
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error getting subscription status:', error);
    throw new Error(error.message || 'Failed to get subscription status');
  }
};

const paymentService = {
  createCheckoutSession,
  verifyCheckoutSession,
  cancelSubscription,
  getSubscriptionStatus,
  PAYMENT_PLANS
};

export default paymentService;